import {useCallback, useEffect, useRef, type RefObject} from 'react';

import {FOCUSABLE_SELECTOR} from 'lib/focusUtils';

/**
 * Non-trapping: focuses the first focusable in `ref` when `enabled`; returns `restoreFocus`,
 * which returns the element it tried to send focus back to.
 */
export default function useAutoFocusReturn<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  enabled: boolean,
  preventScroll = false
): () => HTMLElement | null {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const restoreFocus = useCallback(() => {
    const target = previouslyFocusedRef.current;
    target?.focus({preventScroll});
    return target;
  }, [preventScroll]);
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- imperative focus is a real side effect */
  useEffect(() => {
    if (!enabled) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus({preventScroll});
  }, [ref, enabled, preventScroll]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */
  return restoreFocus;
}
